import React, { useState } from 'react';
import {
  TableRow,
  TableCell,
  Checkbox,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  LinkOff as UnlinkIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../../helpers/formatCurrency';
import { isReconciled } from '../../helpers/reconciliationHelpers';
import UnreconcileConfirmDialog from './UnreconcileConfirmDialog';

// Helper function to determine if a journal entry is a transfer
const isTransferEntry = (entry) => {
  return entry.journalEntryType === 5;
};

const getStatusInfo = (entry) => {
  if (entry.statusString === 'Pago') {
    return { label: 'Pago', color: 'success', icon: <CheckIcon /> };
  }

  if (entry.statusString === 'Vencido') {
    return { label: 'Vencido', color: 'error', icon: <UncheckedIcon /> };
  }

  if (entry.statusString === 'Pendente') {
    return { label: 'Pendente', color: 'warning', icon: <UncheckedIcon /> };
  }

  // Fallback if statusString is missing
  if (entry.statusPayment === true) {
    return { label: 'Pago', color: 'success', icon: <CheckIcon /> };
  }

  return { label: 'Pendente', color: 'warning', icon: <UncheckedIcon /> };
};

const JournalEntryRow = ({
  entry,
  isSelected,
  onToggle,
  formatDate,
  onUnreconcile,
  onEdit,
  onDelete,
}) => {
  const isReconciledStatus = isReconciled(entry);
  const reconciliationData = entry.reconciliationData;
  const hasOneToOneReconciliation =
    reconciliationData?.bankTransactions?.length === 1 &&
    reconciliationData?.journalEntries?.length === 1;
  const reconciledAmount = hasOneToOneReconciliation
    ? reconciliationData.bankTransactions[0]?.amount
    : null;
  const reconciledDate = hasOneToOneReconciliation
    ? reconciliationData.bankTransactions[0]?.datePosted
    : null;
  const displayValue =
    reconciledAmount !== null && reconciledAmount !== undefined
      ? Math.abs(reconciledAmount)
      : Math.abs(entry.valueFinal ?? entry.value ?? 0);
  const displayDate = reconciledDate || entry.dateVencimento || entry.dateCompetencia;
  const [showUnreconcileDialog, setShowUnreconcileDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleUnreconcileClick = (e) => {
    e.stopPropagation(); // Prevent row selection
    setShowUnreconcileDialog(true);
  };

  const handleConfirmUnreconcile = async () => {
    if (onUnreconcile && entry.reconciliationData?.reconciliationId) {
      await onUnreconcile(entry.reconciliationData.reconciliationId, entry);
      // Data refresh is handled automatically by the parent component via refreshAllData
    }
    setShowUnreconcileDialog(false);
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    if (onEdit) {
      onEdit(entry);
    }
  };

  const handleDelete = () => {
    handleMenuClose();
    if (onDelete) {
      onDelete(entry);
    }
  };

  return (
    <>
      <TableRow
        selected={isSelected}
        sx={{
          '&:hover': { backgroundColor: 'action.hover' },
          opacity: isReconciledStatus ? 0.6 : 1,
          backgroundColor: isSelected ? 'rgba(156, 39, 176, 0.08)' : 'inherit',
          '&.Mui-selected': {
            backgroundColor: 'rgba(156, 39, 176, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(156, 39, 176, 0.16)',
            },
          },
          height: 48, // More compact height
          cursor: 'pointer',
        }}
        onClick={() => !isReconciledStatus && onToggle(entry)}
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              if (!isReconciledStatus) {
                onToggle(entry);
              }
            }}
            disabled={isReconciledStatus}
            size="small"
          />
        </TableCell>

        <TableCell>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            {formatDate(displayDate)}
          </Typography>
        </TableCell>

        <TableCell>
          <Tooltip title={entry.description} placement="top">
            <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', maxWidth: 180 }}>
              {entry.description}
            </Typography>
          </Tooltip>
          {entry.categoryName && (
            <Typography variant="caption" color="text.secondary" display="block">
              {entry.categoryName}
            </Typography>
          )}
        </TableCell>

        <TableCell align="right">
          <Stack alignItems="flex-end" spacing={0.5}>
            <Typography
              variant="body2"
              fontWeight="600"
              sx={{
                fontSize: '0.9rem',
                color: isTransferEntry(entry)
                  ? entry.isInput
                    ? '#4caf50'
                    : '#f44336' // Tom mais suave para transferências
                  : entry.isInput
                  ? 'success.main'
                  : 'error.main',
              }}
            >
              {formatCurrency(displayValue)}
            </Typography>
            <Chip
              icon={getStatusInfo(entry).icon}
              label={getStatusInfo(entry).label}
              size="small"
              color={getStatusInfo(entry).color}
              sx={{
                height: 16,
                fontSize: '0.6rem',
                '& .MuiChip-icon': { fontSize: '0.7rem' },
              }}
            />
          </Stack>
        </TableCell>

        <TableCell>
          <Stack direction="row" alignItems="center" spacing={1}>
              {isReconciledStatus ? (
                <>
                  <Chip
                    icon={<CheckIcon />}
                    label="Conciliado"
                    color="success"
                    size="small"
                    sx={{ fontSize: '0.65rem', height: 20 }}
                  />
                  {onUnreconcile && (
                    <Tooltip title="Desconciliar transação" placement="top">
                      <IconButton
                        size="small"
                        onClick={handleUnreconcileClick}
                        sx={{
                          color: 'warning.main',
                          '&:hover': {
                            backgroundColor: 'warning.light',
                            color: 'warning.dark',
                          },
                          padding: '4px',
                        }}
                      >
                        <UnlinkIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              ) : (
                <Chip
                  icon={<UncheckedIcon />}
                  label="Pendente"
                  color="default"
                  size="small"
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
              )}
              <Tooltip title="Mais ações" placement="top">
                <IconButton
                  size="small"
                  onClick={handleMenuClick}
                  sx={{
                    color: 'action.active',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      color: 'action.focus',
                    },
                    padding: '4px',
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
        </TableCell>
      </TableRow>

      {/* Menu de Ações */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'actions-button',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            '& .MuiMenuItem-root': {
              transition: 'all 0.2s ease-in-out',
              borderRadius: 1,
              margin: '2px 4px',
            },
          },
        }}
      >
        <MenuItem
          onClick={handleEdit}
          sx={{
            color: '#fd7e14',
            '&:hover': {
              backgroundColor: '#fff3cd',
            },
            '& .MuiListItemIcon-root': {
              color: '#fd7e14',
            },
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Editar</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          sx={{
            color: '#dc3545',
            '&:hover': {
              backgroundColor: '#f8d7da',
            },
            '& .MuiListItemIcon-root': {
              color: '#dc3545',
            },
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Deletar</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialog de Confirmação de Desconciliação */}
      <UnreconcileConfirmDialog
        open={showUnreconcileDialog}
        onClose={() => setShowUnreconcileDialog(false)}
        onConfirm={handleConfirmUnreconcile}
        reconciliationData={entry.reconciliationData}
        item={entry}
      />
    </>
  );
};

export default JournalEntryRow;
